import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { useModalEscape } from '../../../../hooks/useModalEscape';
import { BrowserOverlayHostContext } from './browserOverlayHost';

interface IteroBrowserFrameProps {
    titleId: string;
    title: string;
    subtitle: string;
    headerControls: ReactNode;
    toolbar: ReactNode;
    onClose: () => void;
    children: ReactNode;
}

// One fixed size for every state (like AppointmentsModalFrame), clamped to the viewport. z-50 rather
// than ModalShell's z-[60] so shared-ui tooltips that can't take TOOLTIP_OVER_MODAL still show on top.
export function IteroBrowserFrame({
    titleId,
    title,
    subtitle,
    headerControls,
    toolbar,
    onClose,
    children
}: IteroBrowserFrameProps) {
    const subtitleId = `${titleId}-subtitle`;
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [overlayHost, setOverlayHost] = useState<HTMLDivElement | null>(null);
    const attachDialog = useCallback((node: HTMLDivElement | null) => {
        dialogRef.current = node;
        setOverlayHost(node);
    }, []);
    useModalEscape(onClose);
    useDialogFocus(dialogRef);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black bg-opacity-50">
            <div
                ref={attachDialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${titleId} ${subtitleId}`}
                tabIndex={-1}
                className="relative w-full max-w-[1240px] h-[min(85vh,860px)] flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden focus:outline-none"
            >
                <div className="flex h-[60px] shrink-0 items-center gap-4 border-b border-gray-200 px-5">
                    <div className="flex min-w-0 flex-1 flex-col">
                        <h2
                            id={titleId}
                            className="truncate text-[15px] font-semibold leading-5 text-gray-900"
                            title={title}
                        >
                            {title}
                        </h2>
                        <p id={subtitleId} className="truncate text-[12px] leading-4 text-gray-500">
                            {subtitle}
                        </p>
                    </div>
                    {headerControls}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="h-9 w-9 shrink-0 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                    >
                        <XMarkIcon className="h-[17px] w-[17px]" />
                    </button>
                </div>
                {toolbar}
                <BrowserOverlayHostContext.Provider value={overlayHost}>
                    {children}
                </BrowserOverlayHostContext.Provider>
            </div>
        </div>,
        document.body
    );
}
